import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'

import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
  storePKCEParams,
} from '../../../../utils/pkceUtils'

import { useSearchParams } from 'react-router-dom'
import { isMacOS } from '../../../../utils/browserDetect'
import { featureVideo, kaiData } from '../../constants'

interface OnboardingFormProps {
  scene: string
  showMembers: boolean
  setShowLogo: (show: boolean) => void
}

interface MessageData {
  type: string
  url: string
}

const SHOW_SIGNUP_TIME = 135
const LOOP_START = 142
const LOOP_END = 150

const { VITE_COMPANION_APP_CLIENT_ID } = import.meta.env

const OnboardingForm: React.FC<OnboardingFormProps> = memo(
  ({ scene, showMembers, setShowLogo }) => {
    const [searchParams] = useSearchParams()
    const isAuthV2 = searchParams.get('auth') === 'v2'
    const codeChallenge = searchParams.get('code_challenge')
    const codeChallengeMethod = searchParams.get('code_challenge_method')
    const useWebm = !isMacOS()

    const [signupUrl, setSignupUrl] = useState<string | null>(null)
    const [showSkipButton, setShowSkipButton] = useState(true)
    const [showIdleVideo, setShowIdleVideo] = useState(false)
    const { settings } = useAuth()

    const featuresVideoRef = useRef<HTMLVideoElement>(null)
    const idleVideoRef = useRef<HTMLVideoElement>(null)
    const iframeRef = useRef<HTMLIFrameElement>(null)

    const handleIframeRedirect = useCallback(
      (e: MessageEvent<MessageData>) => {
        if (!settings?.authority) return

        const authorityOrigin = new URL(settings.authority).origin
        const currentOrigin = window.location.origin

        const isAuthorityOrigin =
          e.origin.toLowerCase() === authorityOrigin.toLowerCase()
        const isSameOrigin =
          e.origin.toLowerCase() === currentOrigin.toLowerCase()

        if (!isAuthorityOrigin && !isSameOrigin) {
          console.warn('Received message from untrusted origin:', e.origin)
          return
        }

        if (e.data?.type === 'externalLogin' && e.data?.url) {
          window.location.href = e.data?.url.includes('napster-companion')
            ? e.data.url
            : `${settings.authority}${e.data.url.slice(1, -1)}`
        }
      },
      [settings],
    )

    const handleIframeLoad = useCallback(() => {
      if (!iframeRef.current) return

      try {
        const iframeLocation = iframeRef.current.contentWindow?.location.href

        if (iframeLocation?.includes('napster-companion')) {
          console.log('Iframe navigated:', iframeLocation)
          window.location.href = iframeLocation
        }
      } catch (error) {
        console.debug(
          'Iframe is on different origin (expected behavior)',
          error,
        )
      }
    }, [])

    const handleFeaturesEnded = () => {
      setShowSkipButton(false)
      setShowIdleVideo(true)
      setShowLogo(false)
      if (featuresVideoRef.current) {
        featuresVideoRef.current.pause()
      }
      if (idleVideoRef.current) {
        void idleVideoRef.current.play()
      }
    }

    const handleTimeUpdate = () => {
      if (featuresVideoRef.current) {
        const currentTime = featuresVideoRef.current.currentTime
        if (currentTime >= SHOW_SIGNUP_TIME) {
          setShowSkipButton(false)
          setShowLogo(false)
        }

        if (currentTime >= LOOP_END) {
          featuresVideoRef.current.currentTime = LOOP_START
        }
      }
    }

    useEffect(() => {
      const initializeAuth = async () => {
        if (!signupUrl && settings) {
          const codeVerifier = generateCodeVerifier()
          let codeChallengeGenerated = await generateCodeChallenge(codeVerifier)
          const state = generateState()

          storePKCEParams(codeVerifier, state)
          let redirectUri = settings.redirect_uri
          let codeChallengeMethodGenerated = 'S256'

          if (isAuthV2) {
            redirectUri = `${window.location.origin}/onboardingcallback`
            codeChallengeGenerated = codeChallenge || ''
            codeChallengeMethodGenerated = codeChallengeMethod || 'S256'
          }

          const authParams = new URLSearchParams({
            client_id: VITE_COMPANION_APP_CLIENT_ID as string,
            redirect_uri: redirectUri,
            response_type: 'code',
            scope: settings.scope || '',
            state,
            code_challenge: codeChallengeGenerated,
            code_challenge_method: codeChallengeMethodGenerated,
            is_transparent: 'true',
            action: 'get-started',
          })

          const url = new URL(
            `${settings.authority}connect/authorize?${authParams.toString()}`,
          )
          setSignupUrl(url.toString())
        }
      }

      void initializeAuth()
    }, [settings, signupUrl])

    useEffect(() => {
      window.addEventListener('message', handleIframeRedirect)
      return () => {
        window.removeEventListener('message', handleIframeRedirect)
      }
    }, [handleIframeRedirect])

    useEffect(() => {
      if (scene === 'form') {
        if (featuresVideoRef.current) {
          featuresVideoRef.current.volume = 0.2
          void featuresVideoRef.current.play()
        }
        if (idleVideoRef.current) {
          void idleVideoRef.current.play()
        }
      }
    }, [scene])

    return (
      <>
        {scene === 'form' && showSkipButton && (
          <button
            className="fixed btn btn-secondary !border-0 top-8 right-8 z-50 !rounded-full"
            onClick={handleFeaturesEnded}
          >
            Skip
          </button>
        )}
        <div
          className={`absolute mt-[-5%] mb-[-5%] flex justify-center transition-opacity duration-700 overflow-hidden ${scene !== 'form' ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        >
          <video
            playsInline
            disablePictureInPicture
            preload="auto"
            className={`w-screen h-screen object-cover object-center pointer-events-none ${showIdleVideo ? 'opacity-0' : 'opacity-100'}`}
            ref={featuresVideoRef}
            onTimeUpdate={handleTimeUpdate}
          >
            <source src={featureVideo} type='video/mp4; codecs="hvc1"' />
          </video>
          <video
            playsInline
            disablePictureInPicture
            autoPlay
            loop
            preload="auto"
            className={`absolute w-auto right-[8vw] h-[130%] mt-[3%] object-cover object-center pointer-events-none ${showIdleVideo ? 'opacity-100 pointer-events-none' : 'opacity-0'}`}
            ref={idleVideoRef}
            onTimeUpdate={handleTimeUpdate}
          >
            {useWebm ? (
              <source src={kaiData.idleWebm} type="video/webm" />
            ) : (
              <source src={kaiData.idle} type='video/mp4; codecs="hvc1"' />
            )}
          </video>
        </div>

        <div
          className={`
            relative z-[101] flex w-full h-full items-center px-24 justify-start 
            ${showSkipButton ? 'opacity-0 pointer-events-none' : 'opacity-100'} 
            ${!showIdleVideo ? 'transition-opacity duration-500' : ''} 
            ${showMembers ? 'hidden' : ''}`}
        >
          {signupUrl && (
            <div className="h-full min-w-[500px] rounded-2xl">
              <iframe
                ref={iframeRef}
                src={signupUrl}
                title="Signup"
                className="h-full w-full"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
                onLoad={handleIframeLoad}
              />
            </div>
          )}
        </div>
      </>
    )
  },
)

export { OnboardingForm }
